import { ChangeDetectorRef, Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { Observable, Subject, throwError } from 'rxjs';
import { tap, takeUntil, catchError } from 'rxjs/operators';

// INTERFACES
import {
  LocalStorageKeys,
  OrderReturnApiService,
  OptionInterface,
} from '../../../shared';
import {
  OrderReturnInterface,
  OrderReturnItemInterface,
  OrderReturnCustomItems,
} from '../../interfaces/order-return.interface';
import { OrderSalesInterface } from '../../interfaces/order-sales.interface';

@Component({
  selector: 'lum-order-return-details',
  templateUrl: './order-return-details.component.html',
  styleUrls: ['./order-return-details.component.scss'],
})
export class OrderReturnDetailsComponent implements OnInit, OnDestroy {
  parentSalesOrderId :number = 0;
  orderReturnId :number = 0;

  restockingFee :number = 0;
  customRefundAmount :number = 0;

  currentParentSalesOrder$ :Observable<OrderSalesInterface>;
  currentOrderReturn$ :Observable<OrderReturnInterface>;
  currentOrderReturnItems$ :Observable<OrderReturnItemInterface[]>;
  loading$ :Observable<boolean>;

  currentParentSalesOrder :OrderSalesInterface;
  currentOrderReturn :OrderReturnInterface;
  currentOrderReturnItems :OrderReturnItemInterface[] = [];

  private onDestroy$ = new Subject();

  constructor(
    private orderReturnService :OrderReturnApiService,
    private route :ActivatedRoute,
    private cd :ChangeDetectorRef,
  ) {}

  ngOnInit() :void {
    this.getDataFromService();
    this.getRouterParams().subscribe();
    this.getParentSalesOrder().subscribe();
    this.getOrderReturn().subscribe();
    this.getOrderReturnItems().subscribe();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  private loadOrderReturn(orderReturnId :number) :void {
    this.orderReturnService.getOrderReturnById({
      orderReturnId,
      params: {
        relations:
          'orderItems.rfq,orderItems.kit,orderItems.orderedUom,orderItems.baseUom,orderItems.rfqStockLog.location,orderItems.rfqStockLog.rfqLot',
      },
    });
  }

  private loadParentSalesOrder(parentSalesOrderId :number) :void {
    this.orderReturnService.getParentSalesOrderById({
      parentSalesOrderId,
      params: {
        relations:
          'orderItems.rfq,orderItems.kit,orderItems.orderedUom,orderItems.baseUom',
      },
    });
  }

  private getDataFromService() :void {
    this.currentParentSalesOrder$ =
      this.orderReturnService.currentParentSalesOrder$;
    this.currentOrderReturn$ = this.orderReturnService.currentOrderReturn$;
    this.currentOrderReturnItems$ =
      this.orderReturnService.currentOrderReturnItems$;
    this.loading$ = this.orderReturnService.loading$;
  }

  private getOrderReturn() :Observable<OrderReturnInterface> {
    return this.currentOrderReturn$.pipe(
      tap((currentOrderReturn) => {
        if (currentOrderReturn) {
          this.currentOrderReturn = currentOrderReturn;
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private getParentSalesOrder() :Observable<OrderSalesInterface> {
    return this.currentParentSalesOrder$.pipe(
      tap((currentParentSalesOrder) => {
        if (currentParentSalesOrder) {
          this.currentParentSalesOrder = currentParentSalesOrder;
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private getOrderReturnItems() :Observable<OrderReturnItemInterface[]> {
    return this.currentOrderReturnItems$.pipe(
      tap((currentOrderReturnItems) => {
        if (currentOrderReturnItems) {
          const orderReturnItems = currentOrderReturnItems;

          const restockingFee = orderReturnItems.find(
            (orderReturnItem :OrderReturnItemInterface) =>
              orderReturnItem?.rfq?.internalSku ===
              OrderReturnCustomItems.RESTOCKING_FEE,
          );

          const customRefundAmount = orderReturnItems.find(
            (orderReturnItem :OrderReturnItemInterface) =>
              orderReturnItem?.rfq?.internalSku ===
              OrderReturnCustomItems.CUSTOM_REFUND_AMOUNT,
          );

          this.restockingFee = restockingFee?.unitPrice || 0;
          this.customRefundAmount = customRefundAmount?.unitPrice || 0;

          const customItems = [
            OrderReturnCustomItems.CUSTOM_REFUND_AMOUNT as string,
            OrderReturnCustomItems.RESTOCKING_FEE as string,
          ];
          this.currentOrderReturnItems = orderReturnItems.filter(
            (orderReturnItem :OrderReturnItemInterface) => {
              return !customItems.includes(orderReturnItem?.rfq?.internalSku || '');
            },
          );
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private getRouterParams() :Observable<Params> {
    return this.route.params.pipe(
      tap((params :Params) => {
        const { parentSalesOrderId, orderReturnId } = params;
        if (parentSalesOrderId) {
          this.parentSalesOrderId = parentSalesOrderId;
          this.loadParentSalesOrder(this.parentSalesOrderId);
        }
        if (orderReturnId) {
          this.orderReturnId = orderReturnId;
          this.loadOrderReturn(this.orderReturnId);
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }
}
